import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-slate-900">Terms of Service</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          <div>
            <p className="text-sm text-slate-600 mb-6">Last Updated: January 1, 2025</p>
          </div>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">1. Acceptance of Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              By accessing and using SmartReqs ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use the Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">2. Description of Service</h2>
            <p className="text-slate-700 leading-relaxed">
              SmartReqs provides AI-powered job requisition generation and market analysis tools. The Service includes access to AI-generated content, market data, and document export capabilities.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">3. User Accounts</h2>
            <p className="text-slate-700 leading-relaxed">
              To use the Service, you must create an account. You are responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Ensuring your account information is accurate and current</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">4. Subscription and Payment</h2>
            <p className="text-slate-700 leading-relaxed">
              Access to SmartReqs requires a paid subscription. By subscribing, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li>Pay all fees associated with your chosen subscription plan</li>
              <li>Provide accurate and complete payment information</li>
              <li><strong>Subscriptions automatically renew</strong> at the end of each billing period unless cancelled</li>
              <li>You may cancel your subscription at any time through your account settings</li>
              <li>Upon cancellation, you retain access until the end of your current billing period</li>
              <li>Subscription fees are non-refundable except as required by law</li>
              <li>We reserve the right to change subscription pricing with 30 days notice</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">5. Acceptable Use</h2>
            <p className="text-slate-700 leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws in your jurisdiction</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service to transmit any malicious code or harmful content</li>
              <li>Resell, redistribute, or share your account access</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">6. Intellectual Property</h2>
            <p className="text-slate-700 leading-relaxed">
              The Service and its original content, features, and functionality are owned by SmartReqs and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of content you create using the Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">7. AI-Generated Content</h2>
            <p className="text-slate-700 leading-relaxed">
              Content generated by our AI tools is provided as-is. While we strive for accuracy, you are responsible for reviewing, editing, and ensuring all generated content meets your needs and complies with applicable laws and regulations before use.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">8. Disclaimer of Warranties</h2>
            <p className="text-slate-700 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">9. Limitation of Liability</h2>
            <p className="text-slate-700 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SMARTREQS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">10. Termination</h2>
            <p className="text-slate-700 leading-relaxed">
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">11. Changes to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">12. Governing Law</h2>
            <p className="text-slate-700 leading-relaxed">
              These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">13. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed">
              If you have any questions about these Terms, please contact us at{" "}
              <a href="mailto:admin@smartreqs.ai" className="text-blue-600 hover:underline">
                admin@smartreqs.ai
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
